import { useRef, useState, useCallback } from 'react';
import { RealtimeClient } from 'openai-realtime-api';

export type SpeechRecognitionEventResult = {
  transcript: string;
};

export type SpeechRecognitionResult = {
  0: SpeechRecognitionEventResult;
  isFinal: boolean;
};

export type SpeechRecognitionResultList = {
  length: number;
  [index: number]: SpeechRecognitionResult;
};

export type SpeechRecognitionEvent = Event & {
  results: SpeechRecognitionResultList;
};

export type SpeechRecognitionErrorEvent = Event & {
  error: string;
};

export type ISpeechRecognition = EventTarget & {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;

  onstart: ((this: ISpeechRecognition, ev: Event) => unknown) | null;
  onresult: ((this: ISpeechRecognition, ev: SpeechRecognitionEvent) => unknown) | null;
  onerror: ((this: ISpeechRecognition, ev: SpeechRecognitionErrorEvent) => unknown) | null;
  onend: ((this: ISpeechRecognition, ev: Event) => unknown) | null;

  start(): void;
  stop(): void;
};

export type ChatItem = { category: 'question' | 'answer'; text: string };

declare global {
  interface Window {
    SpeechRecognition?: new () => ISpeechRecognition;
    webkitSpeechRecognition?: new () => ISpeechRecognition;
  }
}

export type OnTranscriptFn = (text: string) => void;

export const useRecognition = (onTranscript?: OnTranscriptFn) => {
  const clientRef = useRef<RealtimeClient | null>(null);

  const [speaking, setSpeaking] = useState(false);
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [endCall, setEndCall] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const lastTranscriptRef = useRef('');
  const restartTimeoutRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stopRecognition = useCallback(() => {
    const recognition = recognitionRef.current;

    if (recognition) {
      recognition.onend = null;
      recognition.stop();
      recognitionRef.current = null;
    }

    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }

    setIsListening(false);
  }, []);

  const startRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    if (recognitionRef.current) return;

    const recognition = new SpeechRecognition();

    recognition.lang = 'it-IT';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[event.results.length - 1][0].transcript.trim();

      if (transcript && transcript !== lastTranscriptRef.current) {
        lastTranscriptRef.current = transcript;
        onTranscript?.(transcript);
        setChat(prevChat => [...prevChat, { category: 'question', text: transcript }]);
        clientRef.current?.sendUserMessageContent([{ type: 'input_text', text: transcript }]);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        stopRecognition();
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;

      if (isListening) {
        if (restartTimeoutRef.current) {
          clearTimeout(restartTimeoutRef.current);
        }

        restartTimeoutRef.current = window.setTimeout(() => {
          startRecognition();
        }, 300);
      }
    };

    recognitionRef.current = recognition;
    lastTranscriptRef.current = '';
    recognition.start();
  }, [isListening, onTranscript, stopRecognition]);

  const handleVideoClick = async () => {
    if (!isCameraOn) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480 },
          audio: false,
        });

        streamRef.current = stream;
        setIsCameraOn(true);

        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }, 100);
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert(err);
      }
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setIsCameraOn(false);
    }
  };

  const initClient = async (instructions: string) => {
    const client = new RealtimeClient({
      apiKey: '',
      dangerouslyAllowAPIKeyInBrowser: true,
      sessionConfig: {
        instructions,
      },
      model: 'gpt-4o-realtime-preview',
    });

    clientRef.current = client;

    await client.connect();

    client.on('conversation.item.completed', event => {
      if (event.item.role !== 'assistant') return;

      setChat(prevChat => [
        ...prevChat,
        { category: 'answer', text: event.item.formatted.transcript },
      ]);

      const int16Array = event.item.formatted.audio;
      if (!int16Array || int16Array.length === 0) return;

      const sampleRate = 24000;

      const audioCtx = new AudioContext();

      const buffer = audioCtx.createBuffer(1, int16Array.length, sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < int16Array.length; i++) {
        // eslint-disable-next-line @altermaind/no-magic-numbers
        data[i] = int16Array[i] / 32768;
      }

      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(audioCtx.destination);
      source.start();
      setSpeaking(true);

      source.onended = () => {
        setSpeaking(false);
      };
    });
  };

  return {
    isListening,
    startRecognition,
    stopRecognition,
    isCameraOn,
    setIsCameraOn,
    handleVideoClick,
    videoRef,
    streamRef,
    endCall,
    setEndCall,
    speaking,
    setSpeaking,
    initClient,
    chat,
  };
};
